import { useContext, useMemo, useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import Clickable from 'components/Common/Clickable'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import Heading from 'LuxKit/Typography/Heading'

import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { AnythingCategory } from 'constants/experience'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { arrayToObject } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import styled from 'styled-components'
import { pushWithRegion } from '../../../../actions/NavigationActions'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import useExperienceSearchLabels from 'components/Experiences/hooks/useExperienceSearchLabels'
import useExperienceDateDisplayValue from 'components/Experiences/hooks/useExperienceDateDisplayValue'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'

const SearchContainer = styled(Group)`
  padding-top: ${rem(12)};
  padding-left: ${rem(20)};
  padding-right: ${rem(20)};
`

const SearchButton = styled(TextButton)`
  height: auto;
  padding: ${rem(8)} ${rem(12)};
  justify-content: space-between;
  flex: 1;
`

const MapButton = styled(Clickable)`
  display: flex;
  align-items: center; 
`

interface Props {
  className?: string
  isMapView?: boolean
  searchModeUrl?: string
}

function ExperienceSearchBar({ className, isMapView = false, searchModeUrl }: Props) {
  const intl = useIntl()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const dispatch = useAppDispatch()
  const { anytimePlaceholder } = useExperienceSearchLabels()

  const openSearchDrawer = useCallback(() => { toggleSearchVertical('experiences') }, [toggleSearchVertical])
  const categories = useAppSelector(state => state.experience.experienceCategories)
  const { searchItem, experienceCategories, checkinDate, checkoutDate } = useContext(GlobalSearchStateContext)
  const anythingCategoryLabel = intl.formatMessage({
    id: '_Oew9UlRPRSyH_7lZrQL',
    defaultMessage: 'Anything',
    description: 'Category option label to show all experience categories',
  })

  const selectedCategoryNames = useMemo(() => {
    const categoriesById = arrayToObject(categories, cat => cat.id)
    return experienceCategories
      ?.map(id => {
        if (id === AnythingCategory.id) {
          return anythingCategoryLabel
        }

        return categoriesById[id]?.name
      })
      .filter(Boolean)
      .join(' & ')
  }, [anythingCategoryLabel, categories, experienceCategories])

  const handleBackToListClick = useCallback((e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.stopPropagation()
    if (searchModeUrl) {
      dispatch(pushWithRegion(searchModeUrl))
    }
  }, [dispatch, searchModeUrl])

  const dateFieldDisplayValue = useExperienceDateDisplayValue(checkinDate, checkoutDate, anytimePlaceholder)
  const searchSummary = selectedCategoryNames
    ? intl.formatMessage(
        {
          id: 'pwwj8FAXzaS4-KhYurYI',
          defaultMessage: '{dateFieldDisplayValue} • {selectedCategoryNames}',
          description: 'Summary text shown in the experience search button combining the selected date text with the selected experience category names.',
        },
        { dateFieldDisplayValue, selectedCategoryNames },
      )
    : dateFieldDisplayValue

  return <SearchContainer direction="horizontal" gap={16} className={className}>
    {isMapView && <Clickable onClick={handleBackToListClick}>
      <LineArrowLeftIcon />
    </Clickable>}
    <SearchButton
      kind="secondary"
      variant="dark"
      endIcon={<LineSearchIcon size="M" />}
      onClick={openSearchDrawer}
    >
      <div>
        {searchItem && <Heading variant="heading6" align="start">{searchItem.format.mainText}</Heading>}
        <BodyText variant="medium" colour="neutral-two" align="start">
          {searchSummary}
        </BodyText>
      </div>
    </SearchButton>
    {!isMapView && <MapButton to={searchModeUrl}>
      <VerticalSpacer gap={0}>
        <LineMapIcon />
        <BodyText variant="medium" colour="neutral-two" align="start">{intl.formatMessage({
          id: 'ST9Ky4acKsygt61zpMfT',
          defaultMessage: 'Map',
          description: 'Button text to switch to map view on experience search',
        })}
        </BodyText>
      </VerticalSpacer>
    </MapButton>}
  </SearchContainer>
}

export default ExperienceSearchBar
